import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createWorkOrderAssignmentExpense } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderExpenseMutations";
import { ExpensePaidBy, ExpensePaidTo, ExpenseType } from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { toast } from "sonner";
import { useWorkOrderAssignment } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignment";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { useFormikContext } from "formik";

const expenseTypeOptions = [
  { label: "Fuel", value: ExpenseType.Fuel },
  { label: "Ferry", value: ExpenseType.Ferry },
  { label: "Flight", value: ExpenseType.Flight },
  { label: "Accommodation", value: ExpenseType.Accommodation },
  { label: "Toll", value: ExpenseType.Toll },
  { label: "Transportation", value: ExpenseType.Transportation },
  { label: "Other", value: ExpenseType.Other },
];

const validation = z.object({
  type: z.enum(ExpenseType),
  amount: z.number().min(1, "Amount is required"),
  description: z.string().optional().nullable(),
});

export function AddDriverExpenseDialog({
  workOrderAssignmentId,
}: {
  workOrderAssignmentId: string;
}) {
  const { close } = useDialogState();
  const { mutateAsync: createExpense } = useGqlMutation(
    createWorkOrderAssignmentExpense,
  );
  const assignment = useWorkOrderAssignment(workOrderAssignmentId);
  const defaultCurrency = assignment.workOrder.currency;

  return (
    <BaseDialog title="Add Driver Expense">
      <GenericForm<z.infer<typeof validation>>
        initialValues={{
          type: ExpenseType.Fuel,
          amount: null!,
          description: "",
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          try {
            await createExpense({
              input: {
                workOrderAssignment: { connect: workOrderAssignmentId },
                type: values.type,
                paid_by: ExpensePaidBy.Driver,
                paid_to: ExpensePaidTo.Driver,
                amount: values.amount,
                currency: defaultCurrency,
                description: values.description,
              },
            });
            toast.success("Expense recorded successfully");
            await close();
          } catch {
            toast.error("Failed to create expense");
          }
        }}
      >
        <SelectInput
          name="type"
          label="Expense Type"
          className="col-span-full"
          options={[{ label: "", options: expenseTypeOptions }]}
        />
        <CurrencyInput
          name="amount"
          label="Amount"
          currency={defaultCurrency}
          autoFocus
        />
        <DescriptionField />
        <DialogSubmitButton text="Add Expense" />
      </GenericForm>
    </BaseDialog>
  );
}

function DescriptionField() {
  const { values } = useFormikContext<{ type: ExpenseType }>();

  if (values.type !== ExpenseType.Other) return null;

  return (
    <TextAreaInput
      name="description"
      label="Description"
      className="col-span-full"
    />
  );
}
